<div class="flex flex-col h-full pt-2">
  @if (pricingUnavailableReason) {
    <div
      class="flex flex-col items-center justify-center flex-1 text-center gap-3 py-8"
    >
      <i class="pi pi-info-circle !text-3xl text-muted-color"></i>
      <div class="text-lg font-semibold text-muted-color">
        Pricing not available
      </div>
      <div class="text-sm text-muted-color max-w-md">
        {{ pricingUnavailableReason }}
      </div>
    </div>
  } @else {
    <div class="flex flex-col gap-6">
      @if (pricingModelName) {
        <section>
          <div
            class="text-sm font-semibold uppercase tracking-widest text-muted-color mb-1"
          >
            Pricing Model
          </div>
          <div class="text-base font-semibold">
            {{ pricingModelName }}
          </div>
        </section>
      }

      <!-- Pricing Parameters -->
      @if (hasEditableParameters && selectedCadParameters) {
        <section>
          <div
            class="text-sm font-semibold uppercase tracking-widest text-muted-color mb-3"
          >
            Parameters
          </div>
          <app-parameter-editor
            [parameterSchemas]="parameterSchemas"
            [values]="selectedCadParameters!"
            [idPrefix]="'cad-' + selectedCadObjectId + '-'"
            (parameterChanged)="onParameterEditorChange($event)"
          />
        </section>
      }

      <!-- Calculated Values -->
      @if (
        selectedCadInformation && getInfoKeys(selectedCadInformation).length > 0
      ) {
        <section>
          <div
            class="text-sm font-semibold uppercase tracking-widest text-muted-color mb-3"
          >
            Calculated Values
          </div>
          <div class="flex flex-col">
            @for (
              key of getInfoKeys(selectedCadInformation);
              track key;
              let last = $last
            ) {
              <div
                class="grid grid-cols-2 items-center gap-4 py-2.5"
                [class.border-b]="!last"
                [class.border-surface-switch-100]="!last"
              >
                <span class="text-sm text-surface-switch-600">{{ key }}</span>
                <span class="text-sm font-medium text-right">{{
                  formatPricingValue(selectedCadInformation[key])
                }}</span>
              </div>
            }
          </div>
        </section>
      }

      <!-- Result -->
      @if (pricingResultModel) {
        <section>
          <div
            class="text-sm font-semibold uppercase tracking-widest text-muted-color mb-3"
          >
            Result
          </div>
          <div class="flex flex-col">
            @if (hasResultQuantity) {
              <div class="grid grid-cols-2 gap-4 py-2.5">
                <span class="text-sm text-surface-switch-600"
                  >Total Quantity</span
                >
                <div class="flex flex-col items-end gap-0.5">
                  <span
                    class="text-sm font-semibold text-right flex items-center gap-1.5"
                  >
                    {{ displayQuantity | quantity }}
                  </span>
                  @if (overriddenQuantity) {
                    <span class="text-xs text-muted-color text-right">
                      Overwritten: {{ overriddenQuantity | quantity }}
                    </span>
                  }
                </div>
              </div>
            }
            @if (hasResultUnitCost) {
              <div class="grid grid-cols-2 gap-4 py-2.5">
                <span class="text-sm text-surface-switch-600"
                  >Total Unit Cost</span
                >
                <div class="flex flex-col items-end gap-0.5">
                  <span
                    class="text-sm font-semibold text-right flex items-center gap-1.5"
                  >
                    {{
                      item.calculated_unit_cost ?? item.unit_cost
                        | currency: currencyCode
                    }}
                  </span>
                  @if (isUnitCostOverridden) {
                    <span class="text-xs text-muted-color text-right">
                      Overwritten:
                      {{ item.unit_cost_override | currency: currencyCode }}
                    </span>
                  }
                </div>
              </div>
            }
          </div>
        </section>
      } @else if (selectedCadObjectId && !selectedCadInformation) {
        <div class="text-sm text-muted-color text-center py-4">
          No pricing information available. Save parameters to calculate.
        </div>
      }

      <!-- Manual price override -->
      @if (
        supportsUnitCostOverride || supportsQuantityOverride || isOverridden
      ) {
        <section class="border-t border-surface-switch-200 pt-4">
          <div class="flex items-center justify-between gap-2 mb-3">
            <div
              class="text-sm font-semibold uppercase tracking-widest text-muted-color"
            >
              Price Override
            </div>
            <div class="flex items-center gap-2">
              @if (showOverrideSavedMessage) {
                <span class="text-xs text-green-600 font-medium">Saved!</span>
              }
              @if (isOverridden) {
                <span
                  class="text-xs font-medium text-amber-600 flex items-center gap-1"
                >
                  <i class="pi pi-pencil text-xs"></i> Overridden
                </span>
              }
            </div>
          </div>

          @if (!showOverridePanel) {
            @if (isOverridden) {
              <div class="flex flex-col gap-1.5 text-sm">
                @if (item.unit_cost_override != null) {
                  <div class="flex justify-between gap-4">
                    <span class="text-surface-switch-600">Unit cost</span>
                    <span class="flex items-baseline gap-2">
                      @if (item.calculated_unit_cost != null) {
                        <span class="text-xs text-muted-color line-through">{{
                          item.calculated_unit_cost | currency: currencyCode
                        }}</span>
                      }
                      <span class="font-medium">{{
                        item.unit_cost_override | currency: currencyCode
                      }}</span>
                    </span>
                  </div>
                }
                @if (overriddenQuantity; as overriddenQty) {
                  <div class="flex justify-between gap-4">
                    <span class="text-surface-switch-600">Quantity</span>
                    <span class="flex items-baseline gap-2">
                      @if (item.calculated_quantity != null) {
                        <span class="text-xs text-muted-color line-through">{{
                          displayQuantity | quantity
                        }}</span>
                      }
                      <span class="font-medium">{{
                        overriddenQty | quantity
                      }}</span>
                    </span>
                  </div>
                }
                @if (item.override_note) {
                  <div class="text-muted-color italic mt-1">
                    "{{ item.override_note }}"
                  </div>
                }
              </div>
              <div class="flex gap-2 mt-3">
                <p-button
                  label="Edit override"
                  size="small"
                  [outlined]="true"
                  icon="pi pi-pencil"
                  (onClick)="openOverridePanel()"
                />
                <p-button
                  label="Revert to auto"
                  size="small"
                  severity="secondary"
                  [text]="true"
                  icon="pi pi-undo"
                  [loading]="savingOverride"
                  (onClick)="clearOverride()"
                />
              </div>
            } @else {
              <p-button
                [label]="overrideCallToActionLabel"
                size="small"
                [outlined]="true"
                icon="pi pi-pencil"
                (onClick)="openOverridePanel()"
              />
            }
          } @else {
            <div class="flex flex-col gap-3">
              @if (supportsUnitCostOverride) {
                <div class="flex flex-col gap-1">
                  <label class="text-sm text-surface-switch-600"
                    >Correct unit cost</label
                  >
                  <p-inputNumber
                    [(ngModel)]="overrideDraft.unitCost"
                    mode="currency"
                    [currency]="currencyCode"
                    [minFractionDigits]="2"
                    [maxFractionDigits]="2"
                    [min]="0"
                    class="w-full"
                    inputStyleClass="w-full"
                  />
                  <span class="text-xs text-muted-color"
                    >Auto-calculated:
                    {{
                      item.calculated_unit_cost ?? item.unit_cost
                        | currency: currencyCode
                    }}</span
                  >
                </div>
              }
              @if (supportsQuantityOverride) {
                <div class="flex flex-col gap-1">
                  <label class="text-sm text-surface-switch-600"
                    >Correct quantity</label
                  >
                  @if (item.quantity_type === QuantityType.HOURLY) {
                    <div class="flex items-center gap-2">
                      <p-floatlabel variant="on" class="flex-1 min-w-0">
                        <p-inputNumber
                          inputId="override-quantity-hours"
                          class="w-full"
                          [(ngModel)]="overrideDraft.quantityHours"
                          (ngModelChange)="onOverrideTimeFieldChange()"
                          [min]="0"
                          [max]="999999"
                          [useGrouping]="false"
                          inputStyleClass="w-full"
                        />
                        <label for="override-quantity-hours">hours</label>
                      </p-floatlabel>
                      <p-floatlabel variant="on" class="flex-1 min-w-0">
                        <p-inputNumber
                          inputId="override-quantity-mins"
                          class="w-full"
                          [(ngModel)]="overrideDraft.quantityMins"
                          (ngModelChange)="onOverrideTimeFieldChange()"
                          [min]="-1"
                          [max]="60"
                          [useGrouping]="false"
                          inputStyleClass="w-full"
                        />
                        <label for="override-quantity-mins">minutes</label>
                      </p-floatlabel>
                      <p-floatlabel variant="on" class="flex-1 min-w-0">
                        <p-inputNumber
                          inputId="override-quantity-secs"
                          class="w-full"
                          [(ngModel)]="overrideDraft.quantitySecs"
                          (ngModelChange)="onOverrideTimeFieldChange()"
                          [min]="-1"
                          [max]="60"
                          [useGrouping]="false"
                          inputStyleClass="w-full"
                        />
                        <label for="override-quantity-secs">seconds</label>
                      </p-floatlabel>
                    </div>
                  } @else {
                    <p-inputNumber
                      class="block w-full"
                      [(ngModel)]="overrideDraft.quantity"
                      [min]="0.000001"
                      [max]="999999"
                      [maxFractionDigits]="6"
                      [useGrouping]="false"
                      inputStyleClass="w-full"
                    />
                  }
                  <span class="text-xs text-muted-color"
                    >Auto-calculated: {{ displayQuantity | quantity }}</span
                  >
                </div>
              }
              <div class="flex flex-col gap-1">
                <label class="text-sm text-surface-switch-600"
                  >Reason (optional)</label
                >
                <input
                  pInputText
                  type="text"
                  class="w-full"
                  [(ngModel)]="overrideDraft.note"
                  placeholder="Why is the auto price wrong?"
                />
              </div>
              @if (overrideDraft.error) {
                <p-message severity="error" styleClass="w-full">
                  {{ overrideDraft.error }}
                </p-message>
              }
              <div class="flex items-center justify-end gap-2">
                <p-button
                  label="Cancel"
                  size="small"
                  severity="secondary"
                  [text]="true"
                  (onClick)="cancelOverride()"
                />
                <p-button
                  label="Save override"
                  size="small"
                  icon="pi pi-check"
                  [loading]="savingOverride"
                  (onClick)="saveOverride()"
                />
              </div>
            </div>
          }
        </section>
      }

      <!-- Save & Recalculate -->
      <div
        class="sticky -bottom-1 left-0 right-0 mt-4 -mx-2 px-2 pr-4 py-3 border-t border-surface-switch-200 bg-surface-switch-0"
      >
        @if (savePricingError) {
          <p-message severity="error" styleClass="w-full mb-3">
            {{ savePricingError }}
          </p-message>
        }
        <div class="flex items-center justify-end gap-3">
          @if (showRecalculatedMessage) {
            <span class="text-sm text-green-600 font-medium"
              >Saved successfully!</span
            >
          }
          <p-button
            label="Save & Recalculate"
            icon="pi pi-check"
            (onClick)="savePricingParameters()"
            [loading]="savingPricingParameters"
          />
        </div>
      </div>
    </div>
  }
</div>
